import type { ContentActionMenuItem } from "@setplex/arcana"

import type { ReactNode } from 'react'

import { ContentActionMenu, MenuAnchorSC } from "/app/libraries/arcana/src/blocks/ContentActionMenu/index.ts"
import { IconNames } from "/app/libraries/arcana/src/atoms/Icon/index.ts"

import { MediaTypes } from '@setplex/tria-api'
import { useUnit } from 'effector-react'
import { useCallback, useMemo, useRef } from 'react'
import { t } from '~/core/i18n/i18next'
import { formatTvShowProgramName } from '~/features/analytix/ui/lib'
import { contentAction } from '~/features/analytix/ui/models/content-actions'
import { model as guestModel } from '~/features/subscriber/guest'
import { useContentWatchedAction } from './useContentWatchedAction'

type Props = {
  id?: number
  argTvShowId?: number
  argSeasonId?: number | null
  isWithSeason?: boolean
  description?: string
  tvShowName?: string
  seasonNumber?: number | string | null
  episodeNumber?: number | string | null
  locked?: boolean
  aclBlockedBy?: boolean
  paid?: boolean | null
  watched?: boolean
  onDetailsSelect?: () => void
  children?: ReactNode
}

export const ContentWatchedEpisodeActions = ({
  id,
  argTvShowId,
  argSeasonId,
  isWithSeason,
  description,
  tvShowName,
  seasonNumber,
  episodeNumber,
  locked,
  aclBlockedBy,
  paid,
  watched,
  onDetailsSelect,
  children,
}: Props) => {
  const { guest, trackContentAction } = useUnit({
    guest: guestModel.$guest,
    trackContentAction: contentAction,
  })
  const anchorRef = useRef<HTMLDivElement>(null)
  const programName = useMemo(
    () =>
      formatTvShowProgramName({
        seasonNumber,
        episodeNumber,
        episodeName: description,
      }),
    [description, episodeNumber, seasonNumber]
  )
  const { eligible, menuItem: watchedMenuItem } = useContentWatchedAction({
    id,
    tvShowId: argTvShowId,
    seasonId: argSeasonId,
    isWithSeason,
    contentType: MediaTypes.EPISODE,
    name: description,
    analyticsContentType: 'tvshow',
    analyticsContentName: tvShowName,
    analyticsProgramName: programName,
    locked,
    blockedByAcl: aclBlockedBy,
    hasPlaybackAccess: !paid,
    watched,
  })

  const handleDetailsSelect = useCallback(() => {
    trackContentAction({
      contentId: argTvShowId,
      contentType: 'tvshow',
      contentName: tvShowName,
      programName,
      uiElementType: 'menu_option',
      uiElementName: 'details',
      actionName: 'tap',
    })

    onDetailsSelect?.()
  }, [
    argTvShowId,
    onDetailsSelect,
    programName,
    trackContentAction,
    tvShowName,
  ])

  const actionItems = useMemo<ContentActionMenuItem[]>(() => {
    const items: ContentActionMenuItem[] = [
      {
        id: 'details',
        label: t('details'),
        icon: IconNames.info,
        onSelect: handleDetailsSelect,
      },
    ]

    if (watchedMenuItem) {
      items.unshift(watchedMenuItem)
    }

    return items
  }, [handleDetailsSelect, watchedMenuItem])

  const handleMenuOpen = useMemo(() => {
    if (!eligible) return undefined

    return () =>
      trackContentAction({
        contentId: argTvShowId,
        contentType: 'tvshow',
        contentName: tvShowName,
        programName,
        uiElementType: 'button',
        uiElementName: 'kebab_button',
        actionName: 'tap',
      })
  }, [argTvShowId, eligible, programName, trackContentAction, tvShowName])

  const handleMenuDismiss = useMemo(() => {
    if (!eligible) return undefined

    return () =>
      trackContentAction({
        contentId: argTvShowId,
        contentType: 'tvshow',
        contentName: tvShowName,
        programName,
        uiElementType: 'menu',
        uiElementName: 'content_context_menu',
        actionName: 'dismiss',
      })
  }, [argTvShowId, eligible, programName, trackContentAction, tvShowName])

  if (!id || !argTvShowId) return <>{children ?? null}</>
  if (locked || aclBlockedBy) return <>{children ?? null}</>
  if (guest || paid) return <>{children ?? null}</>

  if (!actionItems.length) return <>{children ?? null}</>

  return (
    <MenuAnchorSC ref={anchorRef}>
      {children ?? null}
      <ContentActionMenu
        anchorRef={anchorRef}
        items={actionItems}
        ariaLabel={t('more')}
        onOpen={handleMenuOpen}
        onDismiss={handleMenuDismiss}
      />
    </MenuAnchorSC>
  )
}
